// src/pages/JournalPage.js — v3 · Fusion Journal + OniTalk
// Le joueur écrit sa session -> Claude génère la chronique ET détecte
// les événements démoniaques correspondants -> stats + XP mis à jour

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { useClaude, ClaudeError } from "../hooks/useClaude";
import { PING_EVENTS, STAT_NAMES_FR } from "../data/pingEvents";
import EmptyState from "../components/EmptyState";
import "./JournalPage.css";

// ── Liste compacte des events pour le prompt ──────────────────────────
const EVENTS_INDEX = PING_EVENTS.map(e =>
  `${e.id}: "${e.label}" — ${e.desc}`
).join("\n");

function buildChroniclePrompt(rawText, oniName, oniJp, perks = []) {
  return `Tu es le Chroniqueur Démoniaque de Takkyu Oni. Tu transformes des notes d'entraînement en chronique courte, ET tu identifies quels événements démoniaques se sont produits.

Le joueur est un "${oniName}" (${oniJp}).
Ses perks : ${perks.length ? perks.join(", ") : "non définis"}.

ÉVÉNEMENTS POSSIBLES (choisis 0 à 3 qui correspondent vraiment à la note, ne force rien) :
${EVENTS_INDEX}

Notes brutes du joueur : "${rawText}"

Génère en JSON strict :
{
  "title": "3-5 mots max, percutant, dans le lore",
  "chronicle": "UNE SEULE PHRASE. Max 15 mots. 3e personne. Dramatique.",
  "verdict": "UN mot : VICTOIRE / DÉFAITE / PROGRESSION / CHAOS / DISCIPLINE / RÉVÉLATION / HONTE",
  "demon_quote": "Citation ultra-courte du démon, 8 mots max",
  "event_ids": ["id1", "id2"]
}
- event_ids: liste des IDs d'événements (parmi ceux donnés ci-dessus) qui matchent VRAIMENT la note. Liste vide si rien ne correspond clairement. Maximum 3.
JSON uniquement. Aucun texte autour.`;
}

const VERDICT_STYLES = {
  VICTOIRE:    { color:"#4a9e6b", bg:"rgba(74,158,107,0.1)",  icon:"⚔️" },
  DÉFAITE:     { color:"#c0392b", bg:"rgba(192,57,43,0.1)",   icon:"💀" },
  PROGRESSION: { color:"#5b8dd9", bg:"rgba(91,141,217,0.1)",  icon:"📈" },
  CHAOS:       { color:"#b07fe8", bg:"rgba(176,127,232,0.1)", icon:"🌀" },
  DISCIPLINE:  { color:"#7ecec4", bg:"rgba(126,206,196,0.1)", icon:"⚖️" },
  RÉVÉLATION:  { color:"#d4a017", bg:"rgba(212,160,23,0.1)",  icon:"👁️" },
  HONTE:       { color:"#e67e22", bg:"rgba(230,126,34,0.1)",  icon:"🔥" },
};

function getStyle(verdict) {
  return VERDICT_STYLES[verdict?.toUpperCase()] || VERDICT_STYLES.CHAOS;
}

// Clamp stat entre 5 et 99
function clampStat(v) { return Math.min(99, Math.max(5, Math.round(v))); }

// Applique les statMods avec resistance progressive
function applyMods(currentStats, events) {
  const result = { ...currentStats };
  for (const evt of events) {
    for (const [stat, mod] of Object.entries(evt.statMods)) {
      const current = result[stat] || 50;
      const resistance = mod > 0 ? (current / 99) * 0.6 : ((99 - current) / 99) * 0.6;
      const effective  = mod * (1 - resistance);
      result[stat]     = clampStat(current + effective);
    }
  }
  return result;
}

// ── Chronicle Card — ultra compact ─────────────────────────────────────
export function ChronicleCard({ entry, compact = false }) {
  const style  = getStyle(entry.verdict);
  const [copied, setCopied] = useState(false);

  const shareText = `${entry.oniEmoji || "👹"} ${entry.title}\n${entry.chronicle}\n"${entry.demon_quote}"\n— Takkyu Oni`;
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(shareText).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="chronicle-entry" style={{"--vc": style.color}}>
      <div className="ce-left">
        <span className="ce-icon">{style.icon}</span>
        <div className="ce-date-block">
          <span className="ce-day">
            {new Date(entry.date).toLocaleDateString("fr-FR", {day:"numeric", month:"short"})}
          </span>
          <span className="ce-verdict" style={{color: style.color}}>{entry.verdict}</span>
        </div>
      </div>
      <div className="ce-body">
        <p className="ce-title">{entry.title}</p>
        <p className="ce-chronicle">{entry.chronicle}</p>
        {!compact && entry.demon_quote && (
          <p className="ce-quote">"{entry.demon_quote}"</p>
        )}
        {!compact && entry.eventLabels?.length > 0 && (
          <div className="ce-events">
            {entry.eventLabels.map((l, i) => <span key={i} className="ce-event-tag">{l}</span>)}
          </div>
        )}
      </div>
      <div className="ce-right">
        <span className="ce-xp">+{entry.xp_earned || 15}</span>
        <button className="ce-share" onClick={copy} title="Partager">
          {copied ? "✓" : "↗"}
        </button>
      </div>
    </div>
  );
}

// ── Stat changes panel ──────────────────────────────────────────────────
function StatChanges({ currentStats, newStats }) {
  const changes = Object.entries(newStats)
    .map(([stat, newVal]) => ({
      stat,
      old: currentStats[stat] || 50,
      new: newVal,
      diff: newVal - (currentStats[stat] || 50),
    }))
    .filter(c => Math.abs(c.diff) >= 1)
    .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

  if (changes.length === 0) return null;

  return (
    <div className="impact-stats-panel">
      <p className="impact-stats-title">ÉVOLUTION DES STATS</p>
      {changes.map(({ stat, old, new: newVal, diff }) => (
        <div key={stat} className="impact-stat-row">
          <span className="impact-stat-name">{STAT_NAMES_FR?.[stat] || stat}</span>
          <div className="impact-stat-bar-wrap">
            <div className="impact-stat-bar-bg">
              <div className="impact-stat-bar-old" style={{width: `${old}%`}}/>
              <div
                className={`impact-stat-bar-new ${diff > 0 ? "up" : "down"}`}
                style={{
                  width: `${Math.abs(diff)}%`,
                  left: diff > 0 ? `${old}%` : `${newVal}%`,
                }}
              />
            </div>
          </div>
          <div className="impact-stat-values">
            <span className="impact-stat-old">{old}</span>
            <span className={`impact-stat-arrow ${diff > 0 ? "up" : "down"}`}>
              {diff > 0 ? "↑" : "↓"}
            </span>
            <span className="impact-stat-new">{newVal}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────
export default function JournalPage() {
  const { user, profile, refreshProfile } = useAuth();
  const { generate, loading, error, clearError } = useClaude();
  const [rawText, setRawText]   = useState("");
  const [preview, setPreview]   = useState(null);
  const [saving,  setSaving]    = useState(false);
  const [saved,   setSaved]     = useState(false);
  const [filter,  setFilter]    = useState("ALL");

  const demon    = profile?.demon;
  const oni      = demon?.oni;
  const oniName  = oni?.name  || "Oni";
  const oniJp    = oni?.jp    || "鬼";
  const oniEmoji = oni?.emoji || "👹";
  const perks    = demon?.perks || [];
  const journal  = profile?.journal  || [];
  const currStats = demon?.stats || {};

  const grouped = journal.reduce((acc, entry) => {
    const key = new Date(entry.date).toLocaleDateString("fr-FR", {month:"long", year:"numeric"});
    if (!acc[key]) acc[key] = [];
    acc[key].push(entry);
    return acc;
  }, {});

  const filtered = filter === "ALL"
    ? journal
    : journal.filter(e => e.verdict?.toUpperCase() === filter);

  // ── Génération de la chronique + détection d'événements ──────────────
  const doGenerate = async () => {
    if (!rawText.trim()) return;
    setPreview(null);
    const parsed = await generate({
      messages: [{ role:"user", content: buildChroniclePrompt(rawText, oniName, oniJp, perks) }],
      maxTokens: 500, parseJSON: true,
    });
    if (!parsed) return;

    // Résoudre les events détectés
    const events = (parsed.event_ids || [])
      .map(id => PING_EVENTS.find(e => e.id === id))
      .filter(Boolean)
      .slice(0, 3);

    const newStats = events.length ? applyMods(currStats, events) : currStats;
    const eventXp  = events.reduce((s, e) => s + (e.xp || 0), 0);
    const baseXp   = 15;
    const totalXp  = baseXp + eventXp;

    setPreview({
      ...parsed,
      rawText,
      date: new Date().toISOString(),
      events,
      eventLabels: events.map(e => e.label),
      newStats,
      xp_earned: totalXp,
    });
  };

  // ── Sauvegarde : chronique + stats + XP en une fois ───────────────────
  const save = async () => {
    if (!user || !preview || saving) return;
    setSaving(true);

    const entry = {
      title: preview.title,
      chronicle: preview.chronicle,
      verdict: preview.verdict,
      demon_quote: preview.demon_quote,
      eventLabels: preview.eventLabels,
      xp_earned: preview.xp_earned,
      rawText: preview.rawText,
      date: preview.date,
      oniName, oniJp, oniEmoji,
      id: `${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
      savedAt: new Date().toISOString(),
    };

    const existing  = profile?.journal || [];
    const currentXp = profile?.xp || 0;
    const newCount  = existing.length + 1;
    const newBadges = [...(profile?.badges || [])];
    if (newCount >= 30 && !newBadges.includes("full_journal")) newBadges.push("full_journal");

    // Historique OniTalk pour compat / stats tracking
    const oniTalkEntry = preview.events.length ? {
      date: preview.date,
      eventIds: preview.events.map(e => e.id),
      eventLabels: preview.eventLabels,
      xpEarned: preview.events.reduce((s,e)=>s+(e.xp||0),0),
      statsDelta: Object.fromEntries(
        preview.events.flatMap(e => Object.entries(e.statMods))
          .reduce((acc, [k, v]) => { acc.set(k, (acc.get(k) || 0) + v); return acc; }, new Map())
      ),
    } : null;
    const existingOniTalk = profile?.oniTalkHistory || [];

    const updates = {
      journal: [entry, ...existing].slice(0, 100),
      journalCount: newCount,
      xp: currentXp + preview.xp_earned,
      badges: newBadges,
      lastJournalAt: serverTimestamp(),
    };
    if (preview.events.length) {
      updates["demon.stats"] = preview.newStats;
      updates.oniTalkHistory = [oniTalkEntry, ...existingOniTalk].slice(0, 50);
      updates.lastOniTalkAt = serverTimestamp();
    }

    await updateDoc(doc(db, "users", user.uid), updates);
    await refreshProfile();
    setSaved(true); setPreview(null); setRawText("");
    setSaving(false);
    setTimeout(() => setSaved(false), 3000);
  };

  if (!user) return (
    <div className="journal-page section" style={{paddingTop:"8rem", textAlign:"center"}}>
      <span className="section-eyebrow">鬼語 · OniTalk</span>
      <h2 className="section-title">Chroniques Démoniaques</h2>
      <div className="divider divider-center"/>
      <p style={{color:"var(--ash)", marginBottom:"2rem"}}>Connecte-toi pour accéder à ton journal.</p>
      <Link to="/auth" className="btn btn-primary">ENTRER →</Link>
    </div>
  );

  if (!demon) return (
    <div className="journal-page section" style={{paddingTop:"8rem"}}>
      <EmptyState type="journal_no_demon"/>
    </div>
  );

  return (
    <div className="journal-page">
      <div className="section" style={{paddingTop:"7rem"}}>

        {/* Header */}
        <div className="journal-header">
          <div>
            <span className="section-eyebrow">鬼語 · OniTalk</span>
            <h1 className="section-title">OniTalk</h1>
            <p className="section-sub">
              Note ta session. Le Chroniqueur la forge en légende — et ton démon évolue selon ce que tu lui révèles.
            </p>
          </div>
          <div className="journal-oni-badge">
            <span>{oniEmoji}</span>
            <div>
              <p className="journal-oni-jp">{oniJp}</p>
              <p className="journal-oni-name">{oniName}</p>
            </div>
          </div>
        </div>
        <div className="divider"/>

        <div className="journal-layout">

          {/* Left — Input */}
          <div className="journal-main">
            <div className="journal-input-card card">
              <p className="journal-card-title">NOTES BRUTES DE SESSION</p>
              <textarea
                className="journal-textarea"
                value={rawText}
                onChange={e => setRawText(e.target.value)}
                placeholder={`Ce qui s'est passé, en vrac. "Revers nul, service illégal rentré 3 fois, battu Thomas, raté le smash facile à 10-9, mais je suis resté calme..."`}
                rows={5}
              />
              <div className="journal-input-actions">
                <span className="journal-char-count">{rawText.length} car.</span>
                <button className="btn btn-primary btn-sm" onClick={doGenerate}
                  disabled={loading || rawText.trim().length < 15}>
                  {loading ? <><span className="spinner"/> TON ONI ÉCOUTE...</> : "PARLER À MON ONI →"}
                </button>
              </div>
              <ClaudeError error={error} onRetry={doGenerate} onDismiss={clearError}/>
            </div>

            {/* Preview */}
            {preview && (
              <div className="journal-preview card">
                <p className="journal-card-title">TON ONI A ENTENDU</p>
                <ChronicleCard entry={{...preview, oniEmoji}} />

                {preview.events.length > 0 && (
                  <div className="impact-events-list">
                    {preview.events.map(evt => (
                      <div key={evt.id} className="impact-event-row">
                        <span className="impact-event-label">{evt.label}</span>
                        <p className="impact-event-lore">"{evt.lore}"</p>
                      </div>
                    ))}
                  </div>
                )}

                <StatChanges currentStats={currStats} newStats={preview.newStats}/>

                <div className="journal-preview-actions">
                  <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                    {saving
                      ? <><span className="spinner"/> INSCRIPTION...</>
                      : `${oniEmoji} INSCRIRE +${preview.xp_earned} XP →`}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPreview(null)} disabled={saving}>REJETER</button>
                  <button className="btn btn-ghost btn-sm" onClick={doGenerate} disabled={saving}>↺ REGÉNÉRER</button>
                </div>
              </div>
            )}

            {saved && (
              <div className="journal-saved">✓ Chronique inscrite — ton démon a évolué.</div>
            )}
          </div>

          {/* Right — History */}
          <div className="journal-side">
            <div className="journal-history-header">
              <div>
                <p className="journal-card-title">ARCHIVES · {journal.length} ENTRÉE{journal.length!==1?"S":""}</p>
                <div className="journal-filters">
                  {["ALL","VICTOIRE","DÉFAITE","CHAOS","PROGRESSION"].map(f => (
                    <button key={f}
                      className={`jf-btn${filter===f?" active":""}`}
                      onClick={() => setFilter(f)}>
                      {f==="ALL" ? "TOUT" : f}
                    </button>
                  ))}
                </div>
              </div>
              <Link to={`/u/${user.uid}`} className="btn btn-ghost btn-sm" target="_blank">
                PROFIL PUBLIC ↗
              </Link>
            </div>

            {filtered.length === 0 ? (
              <EmptyState type="journal_empty"/>
            ) : (
              <div className="journal-list">
                {filtered.map((entry, i) => (
                  <ChronicleCard key={entry.id || i} entry={entry} compact />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
